import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiUrl } from '../config/api';
const PRODUCTS_CACHE_KEY = 'aerotubos2025_products_cache';
const CACHE_EXPIRY_KEY = 'aerotubos2025_products_cache_expiry';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Deep comparison function
const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== typeof obj2) return false;
  if (typeof obj1 !== 'object') return obj1 === obj2;
  
  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }
  
  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }
  
  return true;
};

export const useProducts = (token) => {
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [hasChanges, setHasChanges] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tableLocked, setTableLocked] = useState(false);
  const [editedData, setEditedData] = useState({});

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
    if (!expiry) return false;
    return Date.now() < parseInt(expiry);
  }, []);

  // Load products from cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(PRODUCTS_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        setProducts(data);
        setOriginalProducts(JSON.parse(JSON.stringify(data))); // Deep copy
        setEditedData({}); // Clear any previous edits
        setHasChanges(false);
        return true;
      }
    } catch (err) {
      console.error('Error loading from cache:', err);
    }
    return false;
  }, []);

  // Save products to cache
  const saveToCache = useCallback((data) => {
    try {
      localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
    } catch (err) {
      console.error('Error saving to cache:', err);
    }
  }, []);

  // Fetch products from server
  const fetchProducts = useCallback(async (forceRefresh = false) => {
    if (!token) return;

    // Try cache first if not forcing refresh
    if (!forceRefresh && isCacheValid() && loadFromCache()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl('/api/products'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        setOriginalProducts(JSON.parse(JSON.stringify(data))); // Deep copy
        setEditedData({}); // Clear any previous edits
        setHasChanges(false);
        saveToCache(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [token, isCacheValid, loadFromCache, saveToCache]);

  // Load products on mount and when token changes
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Update product field
  const updateProduct = useCallback((productId, field, value) => {
    if (tableLocked) return; // Prevent updates when table is locked
    
    console.log('updateProduct called:', { productId, field, value });
    
    // Update editedData to track changes
    setEditedData(prev => {
      const newEditedData = {
        ...prev,
        [productId]: {
          ...prev[productId],
          [field]: value
        }
      };
      console.log('New editedData:', newEditedData);
      return newEditedData;
    });
    
    // Also update the products array for immediate UI feedback
    setProducts(prevProducts => {
      const updated = prevProducts.map(product => {
        if (product.id === productId) {
          return { ...product, [field]: value };
        }
        return product;
      });
      
      return updated;
    });
  }, [tableLocked]);

  // Check for changes based on editedData
  useEffect(() => {
    const hasChanges = Object.keys(editedData).length > 0;
    setHasChanges(hasChanges);
  }, [editedData]);

  // Save changes to server
  const saveChanges = useCallback(async () => {
    if (!token || !hasChanges || Object.keys(editedData).length === 0) return;

    setTableLocked(true);
    setUpdating(true);
    setError(null);

    try {
      // Send only the edited products to the server
      const editedProducts = Object.keys(editedData).map(id => {
        const product = products.find(p => p.id === parseInt(id));
        return { ...product, ...editedData[id] };
      });

      const response = await fetch(getApiUrl('/api/products'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: editedProducts })
      });

      if (response.ok || response.status === 201) {
        const data = await response.json();
        // Update original products with the saved changes
        setOriginalProducts(JSON.parse(JSON.stringify(products)));
        setEditedData({}); // Clear edited data
        setHasChanges(false);
        saveToCache(products); // Update cache
        return { success: true, message: data.message || 'Changes saved successfully' };
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save changes');
        return { success: false, error: errorData.error };
      }
    } catch (err) {
      const errorMessage = 'Network error. Please check your connection.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setUpdating(false);
      setTableLocked(false);
    }
  }, [token, hasChanges, editedData, products, saveToCache]);

  // Undo changes
  const undoChanges = useCallback(() => {
    if (tableLocked) return; // Prevent undo when table is locked
    setProducts(JSON.parse(JSON.stringify(originalProducts))); // Deep copy
    setEditedData({}); // Clear edited data
    setHasChanges(false);
  }, [originalProducts, tableLocked]);

  // Search and filter products
  const filteredProducts = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') return products;

    const term = searchTerm.toLowerCase().trim();
    return products.filter(product => 
      Object.values(product).some(value => 
        String(value).toLowerCase().includes(term)
      )
    );
  }, [products, searchTerm]);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (!sortField) return filteredProducts;

    return [...filteredProducts].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle different data types
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sortField, sortDirection]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Get sort indicator for a field
  const getSortIndicator = useCallback((field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '▲' : '▼';
  }, [sortField, sortDirection]);

  // Check if product is low stock
  const isLowStock = useCallback((stock) => {
    return stock < 20;
  }, []);

  // Format price as currency
  const formatPrice = useCallback((price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }, []);

  return {
    products: sortedProducts,
    filteredProducts: sortedProducts,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    hasChanges,
    updating,
    tableLocked,
    editedData,
    updateProduct,
    saveChanges,
    undoChanges,
    handleSort,
    getSortIndicator,
    isLowStock,
    formatPrice,
    refetch: fetchProducts
  };
};
